"use client";

import { useState } from "react";
import {
  useCurrentUser,
  useMembers,
  useQuerySearchMembers,
} from "@zapier/client";
import { Avatar } from "@zapier/design-system";
import { isLegacyOwnerId } from "@zapier/utils";
import { FilterSelectMenu, FilterMenuItem } from "../FilterSelectMenu";
import { useNamespacedTranslations } from "../FilterBar/useNamespacedTranslations";
import type { MultiFilterOperator } from "../../../hooks/useRouterParams/multiParameter";

interface Props {
  namespace: string;
  selectedIds: string[];
  operator: MultiFilterOperator;
  onOperatorChange: (operator: MultiFilterOperator) => void;
  onToggle: (id: string, pendingOperator?: MultiFilterOperator) => void;
  /** Whether teams can be selected as an owner. Defaults to `true`. Set to
   * `false` for callers whose owner param only accepts customuser IDs. */
  allowTeams?: boolean;
}

export function OwnerFilterMenu({
  namespace,
  selectedIds,
  operator,
  onOperatorChange,
  onToggle,
  allowTeams = true,
}: Props) {
  const t = useNamespacedTranslations(namespace);
  const [search, setSearch] = useState("");
  const hasSearch = search.trim().length > 0;
  const { user } = useCurrentUser();

  const {
    members: searchedMembers,
    isLoading,
    isFetching,
  } = useQuerySearchMembers({
    search,
    allowTeams,
    enabled: true,
  });

  // Hydrate previously-selected members so they render even when the
  // current typeahead doesn't surface them.
  const selectedNumericIds = selectedIds.filter(isLegacyOwnerId).map(Number);
  const { members: hydratedMembers } = useMembers({
    ids: selectedNumericIds,
  });

  const toMenuItem = (member: {
    id: number;
    fullName: string;
    photoUrl?: string | null;
  }): FilterMenuItem => ({
    id: String(member.id),
    name:
      user?.id === member.id
        ? `${member.fullName} (${t("owner.me-suffix")})`
        : member.fullName,
    renderIcon: () => (
      <Avatar
        name={member.fullName}
        url={member.photoUrl ?? undefined}
        size="small"
      />
    ),
  });

  const byId = new Map<string, FilterMenuItem>();
  const ordered: string[] = [];

  const add = (member: {
    id: number;
    fullName: string;
    photoUrl?: string | null;
  }) => {
    const key = String(member.id);
    if (byId.has(key)) return;
    byId.set(key, toMenuItem(member));
    ordered.push(key);
  };

  if (hasSearch) {
    // While searching show only matches — pinning "me" or previously-selected
    // members alongside results would confuse the search experience.
    for (const member of searchedMembers) add(member);
  } else {
    if (user) {
      const fullName =
        [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
        user.username ||
        t("owner.me");
      add({
        id: user.id,
        fullName,
        photoUrl: user.photoUrl ?? undefined,
      });
    }
    for (const member of searchedMembers) add(member);
    // Fallback: render previously-selected members not surfaced above so they
    // stay visible. FilterSelectMenu already pins items selected at open in a
    // "Selected" section, so we don't reorder them here.
    for (const id of selectedNumericIds) {
      const member = hydratedMembers[id];
      if (member) add(member);
    }
  }

  const items = ordered.map((key) => byId.get(key)!);

  return (
    <FilterSelectMenu
      label={t("owner.label")}
      selectedIds={selectedIds}
      operator={operator}
      availableOperators={["any", "none"]}
      operatorLabels={{
        any: t("owner.operator-any"),
        none: t("owner.operator-none"),
      }}
      items={items}
      search={search}
      isLoading={isLoading}
      isFetching={isFetching}
      searchPlaceholder={t("owner.search-placeholder")}
      noResultsText={t("owner.no-results")}
      onOperatorChange={onOperatorChange}
      onToggle={onToggle}
      onSearchChange={setSearch}
    />
  );
}
